import { ComponentType, Poles } from '../validators';
import { IGNORED_FIELD_META_DISPLAYS } from '../validators/component-sections/metadata';

export type Rating = 'power' | 'energy' | 'voltage' | 'current' | 'breakingCapacity';

export type RatingDirection = 'input' | 'output';

type Steps<Step> = readonly [Step, ...Step[]];

type Measurement = 'power' | 'voltage' | 'current';

export type PortStep =
    | { path: `${Measurement}.${'nom' | 'max' | 'min'}` }
    | { path: 'voltagePerPole.value'; timesPoleCount: true };

type FieldStep = { path: 'energyCapacity.value' | `charging.power.${'nom' | 'max' | 'min'}` };

export type RatingSource =
    | { ports: Steps<PortStep>; ceiling?: true }
    | { fields: Steps<FieldStep> }
    | { firstPort: Steps<FirstPortStep> };

export type FirstPortStep = {
    path: `${'minimumBreakingCapacity' | 'serviceShortCircuitBreakingCapacityIEC' | 'ultimateShortCircuitBreakingCapacityIEC'}.value`;
};

export type RatingRules = {
    default: readonly RatingSource[];
    byType?: { [Type in ComponentType]?: readonly RatingSource[] };
};

export type RatedProduct = {
    type: ComponentType;
    electrical?: {
        ports?: RatedPort[] | null;
        energyCapacity?: { value?: number | null } | null;
        charging?: { power?: Record<string, unknown> | null } | null;
    } | null;
    metadata?: { electrical?: { ports?: (Record<string, unknown> | null)[] | null } | null } | null;
};

export type RatedPort = { powerFlowDirection?: string | null } & {
    [Side in PortSide]?: Record<string, unknown> | null;
};

export const PORT_SIDES = ['DC', 'AC'] as const;

export type PortSide = (typeof PORT_SIDES)[number];

const nominalFirst = <Prefix extends string>(prefix: Prefix) =>
    [{ path: `${prefix}.nom` }, { path: `${prefix}.max` }, { path: `${prefix}.min` }] as const;

export const BREAKER_VOLTAGE_STEPS = [
    { path: 'voltage.max' },
    { path: 'voltage.nom' },
    { path: 'voltagePerPole.value', timesPoleCount: true },
] as const satisfies Steps<PortStep>;

export const CEILING_VOLTAGE_STEPS = [
    { path: 'voltage.max' },
    { path: 'voltage.nom' },
] as const satisfies Steps<PortStep>;

export const PRODUCT_RATINGS: Record<Rating, RatingRules> = {
    power: {
        default: [{ ports: nominalFirst('power') }],
        byType: {
            charger: [{ fields: nominalFirst('charging.power') }, { ports: nominalFirst('power') }],
        },
    },
    voltage: {
        default: [{ ports: nominalFirst('voltage') }],
        byType: {
            breaker: [{ ports: BREAKER_VOLTAGE_STEPS, ceiling: true }],
            fuse: [{ ports: CEILING_VOLTAGE_STEPS, ceiling: true }],
            disconnect: [{ ports: CEILING_VOLTAGE_STEPS, ceiling: true }],
            contactor: [{ ports: CEILING_VOLTAGE_STEPS, ceiling: true }],
        },
    },
    current: { default: [{ ports: [{ path: 'current.nom' }] }] },
    energy: { default: [{ fields: [{ path: 'energyCapacity.value' }] }] },
    breakingCapacity: {
        default: [
            {
                firstPort: [
                    { path: 'minimumBreakingCapacity.value' },
                    { path: 'serviceShortCircuitBreakingCapacityIEC.value' },
                    { path: 'ultimateShortCircuitBreakingCapacityIEC.value' },
                ],
            },
        ],
    },
};

export const typesReadingVoltage = (steps: Steps<PortStep>) =>
    Object.entries(PRODUCT_RATINGS.voltage.byType ?? {})
        .filter(([, sources]) => sources.some((source) => 'ports' in source && source.ports === steps))
        .map(([type]) => type);

export const ceilingTypes = (rating: Rating) =>
    Object.entries(PRODUCT_RATINGS[rating].byType ?? {})
        .filter(([, sources]) => sources.some((source) => 'ceiling' in source && source.ceiling))
        .map(([type]) => type);

export const readRating = (product: RatedProduct, rating: Rating, direction?: RatingDirection) => {
    for (const source of ratingSources(product.type, rating)) {
        const value = readSource(product, source, direction);

        if (value !== undefined) {
            return value;
        }
    }

    return undefined;
};

export const portsFacing = (direction: RatingDirection) => [direction, 'bidirectional'];

const ratingSources = (type: ComponentType, rating: Rating) =>
    PRODUCT_RATINGS[rating].byType?.[type] ?? PRODUCT_RATINGS[rating].default;

const readSource = (product: RatedProduct, source: RatingSource, direction?: RatingDirection) => {
    if ('fields' in source) {
        return source.fields
            .map((step) => positive(valueAt(product.electrical, step.path)))
            .find((value) => value !== undefined);
    }

    if ('firstPort' in source) {
        return readFirstPort(product, source.firstPort);
    }

    return acrossSides(product, direction, (side) =>
        source.ports.map((step) => readPortStep(side, step)).find((value) => value !== undefined),
    );
};

const readPortStep = (side: Record<string, unknown> | null | undefined, step: PortStep) => {
    const value = positive(valueAt(side, step.path));

    if (!('timesPoleCount' in step)) {
        return value;
    }

    const count = Poles.numericValueMap[side?.poles as Poles];

    return value && count ? value * count : undefined;
};

const acrossSides = (
    product: RatedProduct,
    direction: RatingDirection | undefined,
    sideValue: (side: Record<string, unknown> | null | undefined) => number | undefined,
) =>
    largest(
        (product.electrical?.ports ?? [])
            .filter((port) => !direction || portsFacing(direction).includes(port.powerFlowDirection ?? ''))
            .flatMap((port) => PORT_SIDES.filter((side) => port[side]?.enabled).map((side) => sideValue(port[side]))),
    );

const readFirstPort = ({ electrical, metadata }: RatedProduct, steps: Steps<FirstPortStep>) => {
    const port = electrical?.ports?.[0];
    const portMeta = metadata?.electrical?.ports?.[0];

    return largest(
        PORT_SIDES.map((side) =>
            steps
                .map((step) =>
                    flagged(valueAt(portMeta?.[side], `${step.path}.display`))
                        ? undefined
                        : positive(valueAt(port?.[side], step.path)),
                )
                .find((value) => value !== undefined),
        ),
    );
};

const flagged = (display: unknown) => (IGNORED_FIELD_META_DISPLAYS as unknown[]).includes(display);

const largest = (values: unknown[]) => {
    const numbers = values.filter((value): value is number => typeof value === 'number');

    return numbers.length > 0 ? Math.max(...numbers) : undefined;
};

const valueAt = (value: unknown, path: string) =>
    path.split('.').reduce((current, key) => (current as Record<string, unknown> | null | undefined)?.[key], value);

const positive = (value: unknown) => (typeof value === 'number' && value > 0 ? value : undefined);
